import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, Input, OnInit } from '@angular/core';
import { DevicesClass } from '@app/core/classes';
import { RoutingConfig } from '@app/routing/routing.config';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DialogComponentModel, DialogIncludesTypes } from '@app/core/models/diallog';
import { IPartnerProduct } from '@app/modules/partner-modules/partner.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FileToBase64 } from '@app/utils/converter';
import { NotificationService } from '@app/modules/notification-modules/notification.service';
import { PartnerService } from '@app/modules/partner-modules/partner.service';
import { PartnerStoryService } from '@app/modules/partner-modules/partner.story';
import { AppStaticURL } from '@app/app.settings';
import { PartnerTypeModel } from '@app/modules/fundamentals-modules/fundamentals.models';
import { PartnerTypeEnum } from '@app/modules/partner-modules/partner.model';
import { URLLinkValidate } from '@app/utils/validator';
import Sortable from 'sortablejs';

export interface BasePhotoModel {
  rowString: string;
  src: string;
}

export interface BaseErrorModel {
  status: boolean;
  value: string;
}

export interface ProductErrorModels {
  title: BaseErrorModel;
  price: BaseErrorModel;
  productUrl: BaseErrorModel;
  minimumHours: BaseErrorModel;
  description: BaseErrorModel;
}

export enum PartnerProductTypes {
  Create,         // 0
  Edit,           // 1
}

declare var $;

@UntilDestroy()
@Component({
  selector: 'app-partner-crud-product',
  templateUrl: 'crud-product.component.html',
  styleUrls: [`./crud-product.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class CrudProductComponent extends DevicesClass implements OnInit {
  public StaticURL = AppStaticURL;
  public Routing = RoutingConfig;
  public ProductTypes = PartnerProductTypes;
  public PartnerTypes = PartnerTypeEnum;

  /**
   * Base component variables.
   */
  public Product: IPartnerProduct;
  public Partner: PartnerTypeModel;
  public ProductLoading: boolean = false;
  public ProductForm: FormGroup;

  /**
   * Profile photos.
   */
  public ProductPhotos: Array<BasePhotoModel> = [];

  /**
   * Errors view variables
   */
  public ErrorsField: ProductErrorModels;
  public ErrorsText: string;

  /**
   * Special component variables.
   */
  public fileAcceptString = 'image/x-png,image/gif,image/jpeg';
  public maskOptionsPrice = {
    align: 'left',
    prefix: '',
    thousands: ' ',
    decimal: '',
    precision: 0,
    max: 1000000
  };
  public maskOptionsHours = {
    align: 'left',
    prefix: '',
    thousands: ' ',
    decimal: '',
    precision: 0,
    max: 24,
  };

  @Input() Data: any;

  constructor(injector: Injector,
    public cdr: ChangeDetectorRef,
    public ref: DynamicDialogRef,
    private formBuilder: FormBuilder,
    private router: Router,
    private dialogConfig: DynamicDialogConfig,
    private notificationService: NotificationService,
    private partnerService: PartnerService,
    private partnerStory: PartnerStoryService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.ProductForm = this.formBuilder.group({
      title: ['', [Validators.required, Validators.minLength(5)]],
      position: [1],
      price: [null, Validators.required],
      description: ['', [Validators.required, Validators.minLength(5)]],
      minimumHours: [1],
      productUrl: [''],
      picKeys: [[]]
    });

    /**
     * Set of default errors value.
     */
    this.ErrorsField = {
      title: {status: false, value: ''},
      price: {status: false, value: ''},
      productUrl: {status: false, value: ''},
      minimumHours: {status: false, value: ''},
      description: {status: false, value: ''}
    };

    /**
     * Set of base popup settings.
     */
    this.onProductInputConfig();

    /**
     * Form changes control.
     */
    this.onProductFormCtrl();

    /**
     * Set input product's data.
     */
    this.onProductSetData();

    /**
     * Set of drag'n'drop handler.
     */
    this.onSetDragNDrop();
  }

  onProductInputConfig(): void {
    const InputConfig = this.dialogConfig.data as DialogComponentModel || null;

    this.Product = InputConfig.includeData['product'] as any || null;
    this.Partner = InputConfig.includeData['partner'] as any || null;
  }

  onProductFormCtrl(): void {
    for (const field of Object.keys(this.ErrorsField)) {
      this.ProductForm.valueChanges.pipe(untilDestroyed(this)).subscribe(
        value => {
          this.ErrorsField[field].status = false;
          this.ErrorsText = '';
          this.onToggleViewErrors(false);
        }
      );
    }
  }

  onProductSetData(): void {
    if (!this.Product) {
      return;
    }
    for (const key of Object.keys(this.Product)) {
      if (key === 'title') {
        this.productFormCtrl.title.setValue(this.Product[key]);
      }
      if (key === 'description') {
        this.productFormCtrl.description.setValue(this.Product[key]);
      }
      if (key === 'price') {
        this.productFormCtrl.price.setValue(this.Product[key]);
      }
      if (key === 'productUrl') {
        this.productFormCtrl.productUrl.setValue(this.Product[key]);
      }
      if (key === 'minimumHours') {
        this.productFormCtrl.minimumHours.setValue(this.Product[key]);
      }
      if (key === 'photos') {
        if (this.Product[key] && this.Product[key].length) {
          for (const i of Object.keys(this.Product[key])) {
            this.ProductPhotos.unshift({
              rowString: this.Product[key][i].path,
              src: this.StaticURL + this.Product[key][i].path
            });
          }
        }

      }
    }
  }

  /**
   * Upload and remove profile photos handlers.
   */
  onUploadPhotoHandler(): void {
    const UploadInput = document.getElementById('photoUpload');
    UploadInput.click();
  }

  onUploadPhotoEvent(files: FileList): void {
    for (const i of Object.keys(files)) {
      const File: File = files[i];

      if ((File.size / 1024) > 1024) {
        this.ErrorsText = 'Превышен размер загружаемого файла. Ограничение на загрузку - 1Mb.';
        return this.onToggleViewErrors(true);
      }

      FileToBase64(File).then((value: string) => {
        this.partnerService.uploadPic(File).subscribe(
          response => {
            this.ProductPhotos.unshift({
              rowString: response,
              src: value
            });
            this.cdr.markForCheck();

            this.onSetDragNDrop();
          }
        );
      });
    }
  }

  onRemovePhotoEvent(index: number): void {
    this.ProductPhotos.splice(index, 1);
    this.cdr.markForCheck();
  }


  /**
   * Input validate handler.
   */
  onCheckInputValue(type: string): void {
    if (type === 'productUrl') {
      const URL = this.productFormCtrl[type].value;

      if (URL && URL !== '') {
        this.ErrorsField[type].status = !URLLinkValidate(URL);
        this.ErrorsField[type].value = 'Неправильный формат ссылки!'
      }
    }
  }


  /**
   * Drag'n'drop handler.
   */
  onSetDragNDrop(): void {
    setTimeout(() => {
      // const el = document.getElementById('sortable-grid');
      //
      // Sortable.create(el, {
      //   animation: 500,
      //   easing: 'cubic-bezier(1, 0, 0, 1)',
      //   onEnd: (event) => {
      //     const selectedPhoto = this.ProductPhotos[event.oldIndex];
      //
      //     this.ProductPhotos.splice(event.oldIndex, 1);
      //     this.ProductPhotos.splice(event.newIndex, 0, selectedPhoto);
      //   },
      // });
    }, 0);
  }



  onToggleLoading(status: boolean, delay: number = 0) {
    setTimeout(() => {
      this.ProductLoading = status;
      this.cdr.detectChanges();
    }, delay);
  }

  onToggleViewErrors(show: boolean): void {
    const FieldErrors = $('.__product__errors');
    show ? FieldErrors.slideDown(150) : FieldErrors.slideUp(150);
  }

  onCloseDialog(): void {
    this.ref.close()
  }

  onErrorsCallback(error): void {
    const dialogContainer = document.querySelectorAll('.p-dialog') || [];

    /**
     * Reset loading spinner.
     */
    this.onToggleLoading(false);

    if (error.statusCode === 400) {
      return this.notificationService.onDialog({
        type: DialogIncludesTypes.PopupErrorTemplate,
        header: 'Ошибка',
        content: error.message
      });
    }

    if (error.hasOwnProperty('failures')) {
      for (const key of Object.keys(error.failures)) {
        const Failures = error.failures[key];

        if (this.ErrorsField.hasOwnProperty(key)) {
          this.ErrorsField[key].status = true;
          this.ErrorsField[key].value = Failures;
        }
      }
    }

    if (error.hasOwnProperty('message')) {
      this.ErrorsText = error.message + '!';
      this.onToggleViewErrors(true);
    }
  }

  onSubmitForm(): void {
    if (this.ProductLoading) {
      return;
    }

    /**
     * Validation of Errors Fields.
     */
    for (const field of Object.keys(this.ErrorsField)) {
      if (this.ErrorsField[field].status) {
        this.ErrorsText = 'Проверьте правильность заполненных полей!';
        return this.onToggleViewErrors(true);
      }
    }

    /**
     * Validation of Base Fields.
     */
    if (!this.ProductForm.valid) {
      for (const control of Object.keys(this.productFormCtrl)) {
        if (this.ErrorsField.hasOwnProperty(control) && !this.productFormCtrl[control].valid) {
          this.ErrorsField[control].status = true;
        }
      }
      this.ErrorsText = 'Проверьте правильность заполненных полей!';
      return this.onToggleViewErrors(true);
    }

    /**
     * Loading
     */
    this.onToggleLoading(true);


    /**
     * Submit request
     */
    // const photosArray = this.ProductPhotos.map((item, index) => {
    //   return {
    //     position: index,
    //     key: item.rowString
    //   }
    // });
    const photosArray = this.ProductPhotos.map(item => item.rowString);
    const productData = {
      title: this.ProductForm.getRawValue().title,
      position: this.ProductForm.getRawValue().position,
      price: this.ProductForm.getRawValue().price,
      description: this.ProductForm.getRawValue().description,
      productUrl: this.ProductForm.getRawValue().productUrl,
      minimumHours: this.ProductForm.getRawValue().minimumHours === 0 ? 1 : this.ProductForm.getRawValue().minimumHours,
      picKeys: photosArray
    } as IPartnerProduct;


    /**
     * Create new product
     */
    if (this.onProductEventType === this.ProductTypes.Create) {
      this.partnerService.addPartnerProducts(productData).subscribe(
        response => this.onProductEventStatus(true),
        error => {
          this.onErrorsCallback(error);
        }
      );
    }

    /**
     * Edit of product
     */
    if (this.onProductEventType === this.ProductTypes.Edit) {
      this.partnerService.editPartnerProducts(Object.assign({id: this.Product.id}, productData)).subscribe(
        response => this.onProductEventStatus(true),
        error => {
          this.onErrorsCallback(error);
        }
      );
    }
  }

  onProductEventStatus(status: boolean): void {
    this.onCloseDialog();

    if (this.onProductEventType === this.ProductTypes.Create) {
      this.partnerStory.PartnerEventBus.next({
        event: status ? 'onProductAddSuccess' : 'onProductAddError',
        data: null
      })
    }

    if (this.onProductEventType === this.ProductTypes.Edit) {
      this.partnerStory.PartnerEventBus.next({
        event: status ? 'onProductUpdateSuccess' : 'onProductUpdateError',
        data: null
      })
    }
  }

  get productFormCtrl(): any {
    return this.ProductForm.controls;
  }

  get onProductEventType(): PartnerProductTypes {
    return this.Product ? this.ProductTypes.Edit : this.ProductTypes.Create;
  }

  get onProductShopTemplate(): boolean {
    return (
      this.Partner.value === this.PartnerTypes.Record ||
      this.Partner.value === this.PartnerTypes.Base
    )
  }

  get onProductServiceTemplate(): boolean {
    return (
      this.Partner.value === this.PartnerTypes.Shop ||
      this.Partner.value === this.PartnerTypes.School ||
      this.Partner.value === this.PartnerTypes.Workshop
    )
  }
}
